import { useEffect, useMemo, useState } from "react";
import { createCanvasMeasurer } from "./layout/measure";
import { layoutCardScene } from "./layout/cardScene";
import { CANONICAL_PREVIEW_WIDTH, layoutScene } from "./layout/scene";
import {
  getPendingImageCount,
  imageStore,
  loadSceneFonts,
  preloadCreditImages,
} from "./assets";

/** Load fonts and logos, then lay out the scene (used by export). */
export async function buildCanvasScene({
  credits,
  settings,
  fontStyle,
  blankHeight = 0,
  width = CANONICAL_PREVIEW_WIDTH,
}) {
  await Promise.all([loadSceneFonts(fontStyle), preloadCreditImages(credits)]);
  return layoutScene({
    credits,
    settings,
    fontStyle,
    measurer: createCanvasMeasurer(),
    images: imageStore,
    width,
    blankHeight,
  });
}

/**
 * Live scene for the preview. Re-lays out when credits/fonts change, when a
 * web font finishes loading, and when logo bitmaps arrive.
 */
export function useCanvasScene({
  credits,
  settings,
  fontStyle,
  blankHeight,
  width = CANONICAL_PREVIEW_WIDTH,
}) {
  const [fontsVersion, setFontsVersion] = useState(0);
  const [imagesVersion, setImagesVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadSceneFonts(fontStyle).then(() => {
      if (!cancelled) setFontsVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [fontStyle]);

  useEffect(() => {
    const fonts = typeof document !== "undefined" ? document.fonts : null;
    if (!fonts?.addEventListener) return undefined;
    const onLoaded = () => setFontsVersion((v) => v + 1);
    fonts.addEventListener("loadingdone", onLoaded);
    return () => fonts.removeEventListener("loadingdone", onLoaded);
  }, []);

  useEffect(() => {
    if (!getPendingImageCount(credits)) return undefined;
    let cancelled = false;
    preloadCreditImages(credits).then(() => {
      if (!cancelled) setImagesVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [credits]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const measurer = useMemo(() => createCanvasMeasurer(), [fontsVersion]);

  const scene = useMemo(
    () =>
      layoutScene({
        credits,
        settings,
        fontStyle,
        measurer,
        images: imageStore,
        width,
        blankHeight,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [credits, settings, fontStyle, measurer, imagesVersion, blankHeight, width],
  );

  return { scene, sceneVersion: `${fontsVersion}:${imagesVersion}` };
}

/** Load fonts and logos, then lay out card frames (used by export). */
export async function buildCardCanvasScene({
  credits,
  settings,
  fontStyle,
  resolution,
  width,
  frameHeight,
}) {
  await Promise.all([loadSceneFonts(fontStyle), preloadCreditImages(credits)]);
  return layoutCardScene({
    credits,
    settings,
    fontStyle,
    measurer: createCanvasMeasurer(),
    images: imageStore,
    resolution,
    width,
    frameHeight,
  });
}

/**
 * Live card scene. Re-lays out when credits or fonts change, when a web
 * font finishes loading, and when logo bitmaps arrive.
 */
export function useCanvasCardScene({
  credits,
  settings,
  fontStyle,
  resolution,
  enabled = true,
}) {
  const [fontsVersion, setFontsVersion] = useState(0);
  const [imagesVersion, setImagesVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadSceneFonts(fontStyle).then(() => {
      if (!cancelled) setFontsVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, fontStyle]);

  useEffect(() => {
    if (!enabled) return undefined;
    const fonts = typeof document !== "undefined" ? document.fonts : null;
    if (!fonts?.addEventListener) return undefined;
    const onLoaded = () => setFontsVersion((v) => v + 1);
    fonts.addEventListener("loadingdone", onLoaded);
    return () => fonts.removeEventListener("loadingdone", onLoaded);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !getPendingImageCount(credits)) return undefined;
    let cancelled = false;
    preloadCreditImages(credits).then(() => {
      if (!cancelled) setImagesVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, credits]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const measurer = useMemo(() => (enabled ? createCanvasMeasurer() : null), [enabled, fontsVersion]);

  const scene = useMemo(() => {
    if (!enabled || !measurer) return null;
    return layoutCardScene({
      credits,
      settings,
      fontStyle,
      measurer,
      images: imageStore,
      resolution,
    });
  }, [enabled, credits, settings, fontStyle, measurer, imagesVersion, resolution]);

  return { scene, sceneVersion: `${fontsVersion}:${imagesVersion}` };
}
